import { useRouterState } from "@tanstack/react-router";
import { useEffect } from "react";

const META_PIXEL_ID = "1404850441754094";
const META_PIXEL_SCRIPT_ID = "meta-pixel-script";
const META_PIXEL_SCRIPT_URL = "https://connect.facebook.net/en_US/fbevents.js";
const PRIVATE_PAGE_PATTERN =
  /^\/(?:admin(?:-rechnungen)?|konto|login|registrieren|auftrag-bestaetigen|auftrag-verwalten|rechnung)(?:\/|$)/;

type MetaPixelFunction = {
  (...args: unknown[]): void;
  callMethod?: (...args: unknown[]) => void;
  queue: unknown[][];
  loaded: boolean;
  version: string;
  push: (...args: unknown[]) => void;
};

declare global {
  interface Window {
    _fbq?: MetaPixelFunction;
    fbq?: MetaPixelFunction;
    ltBanner?: {
      show?: () => void;
      consent?: {
        marketing?: boolean;
      };
    };
  }
}

let pixelInitialized = false;
let lastTrackedLocation: string | undefined;

function getMetaPixelQueue() {
  if (window.fbq) return window.fbq;

  const fbq = ((...args: unknown[]) => {
    if (fbq.callMethod) {
      fbq.callMethod(...args);
    } else {
      fbq.queue.push(args);
    }
  }) as MetaPixelFunction;

  fbq.push = (...args: unknown[]) => fbq(...args);
  fbq.loaded = true;
  fbq.version = "2.0";
  fbq.queue = [];

  window.fbq = fbq;
  window._fbq = fbq;

  return fbq;
}

function initializeMetaPixel() {
  const fbq = getMetaPixelQueue();

  if (!document.getElementById(META_PIXEL_SCRIPT_ID)) {
    const script = document.createElement("script");
    script.id = META_PIXEL_SCRIPT_ID;
    script.async = true;
    script.src = META_PIXEL_SCRIPT_URL;
    document.head.appendChild(script);
  }

  // Consent can be granted again after the already initialized pixel was revoked.
  fbq("consent", "grant");
  if (!pixelInitialized) {
    // Send only our explicit page views, without automatic button/form events.
    fbq("set", "autoConfig", false, META_PIXEL_ID);
    fbq("init", META_PIXEL_ID);
    pixelInitialized = true;
  }

  return fbq;
}

/** Loads Meta with marketing consent and measures only public page views. */
export function MetaPixel() {
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  useEffect(() => {
    const syncWithConsent = () => {
      const hasMarketingConsent = window.ltBanner?.consent?.marketing === true;

      if (!hasMarketingConsent || PRIVATE_PAGE_PATTERN.test(pathname)) {
        // A slow SDK must not replay pending page views after consent is revoked.
        if (window.fbq && !window.fbq.callMethod) {
          window.fbq.queue = window.fbq.queue.filter(([command]) => command !== "track");
        }
        window.fbq?.("consent", "revoke");
        lastTrackedLocation = undefined;
        return;
      }

      const fbq = initializeMetaPixel();
      if (lastTrackedLocation !== pathname) {
        fbq("track", "PageView");
        lastTrackedLocation = pathname;
      }
    };

    syncWithConsent();
    // Classic deferred consent scripts finish before DOMContentLoaded. If the
    // async application entry hydrates first, pick up returning users' consent
    // after those scripts have initialized, without permitting early tracking.
    document.addEventListener("DOMContentLoaded", syncWithConsent, { once: true });
    window.addEventListener("ltBanner:ConsentChanged", syncWithConsent);

    return () => {
      window.removeEventListener("ltBanner:ConsentChanged", syncWithConsent);
      document.removeEventListener("DOMContentLoaded", syncWithConsent);
    };
  }, [pathname]);

  return null;
}
